import { useNavigate } from 'react-router-dom'

import { useLocalStorageBool } from '@/utils/localStorage'
import {
  Badge,
  Button,
  CardColors,
  CardVariants,
  HeroCard,
  Icon,
  Text,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  faChevronRight,
  faChartLineUp,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons'
import { faStar } from '@fortawesome/pro-solid-svg-icons'
import { useReselector } from '@/utils/sharedHooks'
import {
  FETCH_USER_INCOME_PLANS_KEY,
  selectUserIncomePlanState,
} from '@/features/income-planning/incomePlanning.slice'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'

const INCOME_PLANNING_CREATE_PROMPT_DISMISSED_KEY =
  'INCOME_PLANNING_CREATE_PROMPT_DISMISSED_KEY'
const INCOME_PLANNING_OVERVIEW_PROMPT_DISMISSED_KEY =
  'INCOME_PLANNING_OVERVIEW_PROMPT_DISMISSED_KEY'

export const IncomePlanningMonthIntroCards = () => {
  const navigate = useNavigate()
  const { isTrue: createPromptDismissed, setTrue: setCreatePromptDismissed } =
    useLocalStorageBool(INCOME_PLANNING_CREATE_PROMPT_DISMISSED_KEY)
  const {
    isTrue: incomeOverviewDismissed,
    setTrue: setIncomeOverviewDismissed,
  } = useLocalStorageBool(INCOME_PLANNING_OVERVIEW_PROMPT_DISMISSED_KEY)

  const incomePlaningMonths = useReselector(selectUserIncomePlanState)

  const incomeMonthsLoading = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_INCOME_PLANS_KEY
  )

  if (incomeMonthsLoading) {
    return null
  }

  if (!createPromptDismissed && !Object.values(incomePlaningMonths).length) {
    return (
      <HeroCard
        variant={CardVariants.LEFT_ALIGNED}
        imageAlt="ledger"
        imageSrc="https://heard-images.s3.amazonaws.com/assets/ledger.svg"
        title="Using Income Planning"
        subheader="Income Planning"
        description="Having a clear picture of your financial projections is essential for making informed business decisions. The first step is creating a plan."
        onClose={setCreatePromptDismissed}
        color={CardColors.EMPHASIS}
        className="mb-10"
      >
        <div className="flex items-start gap-4 mt-4">
          <Badge color="olive" size="md" variant="secondary">
            <Text variant={TypographyStyles.BODY_LG}>
              <Icon icon={faStar} color="text-brand" />{' '}
            </Text>
          </Badge>
          <div>
            <Text variant={TypographyStyles.LABEL_MD}>Create a plan</Text>
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              Plans have a built-in calculator that can help you plan for
              multiple income streams. Create plans for the different scenarios
              that your practice encounters whether that’s busy months, summer
              months or holidays.
            </Text>
          </div>
        </div>
        <Button
          iconRight={faChevronRight}
          onClick={() => navigate('/income-planning/create-plan')}
          className="mt-4"
          variant="secondary"
        >
          Create Plan
        </Button>
        {/*todo ZEN-2164 there's a walkthrough link here*/}
      </HeroCard>
    )
  } else if (!incomeOverviewDismissed) {
    return (
      <HeroCard
        variant={CardVariants.LEFT_ALIGNED}
        imageAlt="graph"
        imageSrc="https://heard-images.s3.amazonaws.com/assets/insights_illustration.svg"
        title="Understanding the income planning overview"
        subheader="Income Planning"
        description="This is the main page for income planning. You’ll be able to see your plans at a month-to-month level, and create new plans."
        onClose={setIncomeOverviewDismissed}
        color={CardColors.EMPHASIS}
        className="mb-10"
      >
        <div className="flex items-start gap-4 mt-4">
          <Badge color="olive" size="md" variant="secondary">
            <Text variant={TypographyStyles.BODY_LG}>
              <Icon icon={faChartLineUp} color="text-brand" />
            </Text>
          </Badge>
          <div>
            <Text variant={TypographyStyles.LABEL_MD}>
              Check your performance
            </Text>
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              See how your plan tracks against your prior year income (if
              available), and your actual current year income.
            </Text>
          </div>
        </div>
        <div className="flex items-start gap-4 mt-4">
          <Badge color="olive" size="md" variant="secondary">
            <Text variant={TypographyStyles.BODY_LG}>
              <Icon icon={faWandMagicSparkles} color="text-brand" />
            </Text>
          </Badge>
          <div>
            <Text variant={TypographyStyles.LABEL_MD}>Quick edits</Text>
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              Make quick edits to planned incomes by editing directly in the
              table, or selecting multiple months to apply a plan to.
            </Text>
          </div>
        </div>
        {/*todo ZEN-2164 there's a walkthrough link here*/}
      </HeroCard>
    )
  }

  return null
}
